import { useEffect, useRef, useState } from 'react';
import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision';
import type { BlurEngine } from '../utils/deviceCapabilities';
import { getSelfieSegBlurIntensity, getWebGL2BlurIntensity } from '../utils/blurConfig';

interface UseBackgroundBlurArgs {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  engine: BlurEngine | null;
  enabled: boolean;
  /** Informational fallback dims for the returned `dims` field only — used by
   *  consumers to size parent containers before the camera reports dimensions.
   *  The canvas pixel buffer and GL/segmenter resources are NEVER sized from
   *  these; they wait for the camera's real videoWidth/videoHeight. This is
   *  intentional: canvas.captureStream() encodes any mid-stream canvas resize
   *  as a resolution change in the recorded video, which corrupts playback. */
  width: number;
  height: number;
}

interface UseBackgroundBlurResult {
  isReady: boolean;
  /** True once the engine has rendered at least one frame for the current session.
   *  Resets when enabled toggles or the camera restarts. Use this to decide when
   *  to swap the blur canvas on top of the live video — prevents black/stale flashes. */
  hasFrame: boolean;
  error: string | null;
  /** Actual camera dims after probe, useful for sizing parent containers. */
  dims: { width: number; height: number };
}

function disposeGL(res: GLResources): void {
  const { gl, blurProgram, compositeProgram, videoTexture, maskTexture, pingFBO, pingTexture, pongFBO, pongTexture, vao } = res;
  gl.deleteProgram(blurProgram);
  gl.deleteProgram(compositeProgram);
  gl.deleteTexture(videoTexture);
  gl.deleteTexture(maskTexture);
  gl.deleteTexture(pingTexture);
  gl.deleteTexture(pongTexture);
  gl.deleteFramebuffer(pingFBO);
  gl.deleteFramebuffer(pongFBO);
  gl.deleteVertexArray(vao);
}

/**
 * Snapshots GPU/browser identifiers for failure diagnostics. Logged alongside
 * any blur init/runtime failure so we can correlate by device class in
 * CloudWatch RUM (e.g. "Mali-G52" → known float-buffer gap, falls back).
 */
function getWebGL2DiagnosticInfo(): Record<string, string> {
  const info: Record<string, string> = {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  };
  try {
    const probe = document.createElement('canvas');
    const gl = probe.getContext('webgl2');
    if (gl) {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      info.vendor = String(
        gl.getParameter(dbg ? dbg.UNMASKED_VENDOR_WEBGL : gl.VENDOR)
      );
      info.renderer = String(
        gl.getParameter(dbg ? dbg.UNMASKED_RENDERER_WEBGL : gl.RENDERER)
      );
      info.hasFloat32 = String(!!gl.getExtension('EXT_color_buffer_float'));
      info.hasFloat16 = String(!!gl.getExtension('EXT_color_buffer_half_float'));
    }
  } catch {
    /* diagnostics best-effort — never throw from logging */
  }
  return info;
}

/* =========================================================================
 * WebGL2 engine — MediaPipe Tasks Vision + custom shader pipeline.
 * Mirrors the proven panel implementation, stripped of benchmark concerns.
 * ========================================================================= */

const VERTEX_SHADER = `#version 300 es
  layout(location = 0) in vec2 a_position;
  layout(location = 1) in vec2 a_texCoord;
  out vec2 v_texCoord;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
  }
`;

const BLUR_SHADER = `#version 300 es
  precision mediump float;
  in vec2 v_texCoord;
  out vec4 fragColor;
  uniform sampler2D u_image;
  uniform vec2 u_resolution;
  uniform vec2 u_direction;

  void main() {
    vec2 texel = 1.0 / u_resolution;
    vec4 color = vec4(0.0);
    float weights[5];
    weights[0] = 0.227027;
    weights[1] = 0.1945946;
    weights[2] = 0.1216216;
    weights[3] = 0.054054;
    weights[4] = 0.016216;

    color += texture(u_image, v_texCoord) * weights[0];
    for (int i = 1; i < 5; i++) {
      vec2 offset = u_direction * texel * float(i) * 2.0;
      color += texture(u_image, v_texCoord + offset) * weights[i];
      color += texture(u_image, v_texCoord - offset) * weights[i];
    }
    fragColor = color;
  }
`;

// Composite shader is kept byte-identical to the benchmark panel
// (MediaPipeWebGLPanel.tsx) so production output matches the proven reference
// exactly. `u_sharpen` is exposed but renderBlurComposite always sets it to
// 0.0 — the smoothstep path is reserved for image-background mode (not used
// in production blur), so output is mathematically the same as a plain mix.
const COMPOSITE_SHADER = `#version 300 es
  precision mediump float;
  in vec2 v_texCoord;
  out vec4 fragColor;
  uniform sampler2D u_sharp;
  uniform sampler2D u_blurred;
  uniform sampler2D u_mask;
  uniform float u_sharpen;

  void main() {
    float confidence = texture(u_mask, v_texCoord).r;
    float sharpened = smoothstep(0.35, 0.65, confidence);
    confidence = mix(confidence, sharpened, u_sharpen);
    vec4 sharp = texture(u_sharp, v_texCoord);
    vec4 blurred = texture(u_blurred, v_texCoord);
    fragColor = mix(blurred, sharp, confidence);
  }
`;

interface GLResources {
  gl: WebGL2RenderingContext;
  blurProgram: WebGLProgram;
  compositeProgram: WebGLProgram;
  videoTexture: WebGLTexture;
  maskTexture: WebGLTexture;
  pingFBO: WebGLFramebuffer;
  pingTexture: WebGLTexture;
  pongFBO: WebGLFramebuffer;
  pongTexture: WebGLTexture;
  vao: WebGLVertexArrayObject;
  maskU8Buffer: Uint8Array;
}

function createShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile failed: ${info}`);
  }
  return shader;
}

function createProgram(gl: WebGL2RenderingContext, vsSrc: string, fsSrc: string): WebGLProgram {
  const vs = createShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const program = gl.createProgram()!;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link failed: ${info}`);
  }
  return program;
}

function createFBOTexture(gl: WebGL2RenderingContext, w: number, h: number) {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const fbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return { fbo, texture: tex };
}

function initGL(canvas: HTMLCanvasElement, w: number, h: number): GLResources {
  const gl = canvas.getContext('webgl2', { alpha: false, antialias: false })!;
  if (!gl) throw new Error('WebGL2 not supported');

  const blurProgram = createProgram(gl, VERTEX_SHADER, BLUR_SHADER);
  const compositeProgram = createProgram(gl, VERTEX_SHADER, COMPOSITE_SHADER);

  const vao = gl.createVertexArray()!;
  gl.bindVertexArray(vao);

  const POS_LOC = 0;
  const TEX_LOC = 1;

  const posBuf = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POS_LOC);
  gl.vertexAttribPointer(POS_LOC, 2, gl.FLOAT, false, 0, 0);

  const texBuf = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, texBuf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(TEX_LOC);
  gl.vertexAttribPointer(TEX_LOC, 2, gl.FLOAT, false, 0, 0);

  gl.bindVertexArray(null);

  const videoTexture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, videoTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const maskTexture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, maskTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const ping = createFBOTexture(gl, w, h);
  const pong = createFBOTexture(gl, w, h);

  return {
    gl, blurProgram, compositeProgram,
    videoTexture, maskTexture,
    pingFBO: ping.fbo, pingTexture: ping.texture,
    pongFBO: pong.fbo, pongTexture: pong.texture,
    vao,
    maskU8Buffer: new Uint8Array(w * h),
  };
}

function renderBlurComposite(
  res: GLResources,
  video: HTMLVideoElement,
  mask: Float32Array,
  maskW: number,
  maskH: number,
  canvasW: number,
  canvasH: number,
  passes: number,
) {
  const { gl, blurProgram, compositeProgram, videoTexture, maskTexture, pingFBO, pingTexture, pongFBO, pongTexture, vao } = res;

  gl.bindVertexArray(vao);

  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, videoTexture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

  if (res.maskU8Buffer.length < mask.length) {
    res.maskU8Buffer = new Uint8Array(mask.length);
  }
  const maskU8 = res.maskU8Buffer;
  for (let i = 0; i < mask.length; i++) {
    maskU8[i] = (mask[i] * 255 + 0.5) | 0;
  }
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, maskTexture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, maskW, maskH, 0, gl.RED, gl.UNSIGNED_BYTE, maskU8.subarray(0, maskW * maskH));
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);

  gl.useProgram(blurProgram);
  gl.uniform2f(gl.getUniformLocation(blurProgram, 'u_resolution'), canvasW, canvasH);

  const PING_UNIT = 2;
  const PONG_UNIT = 3;
  gl.activeTexture(gl.TEXTURE0 + PING_UNIT);
  gl.bindTexture(gl.TEXTURE_2D, pingTexture);
  gl.activeTexture(gl.TEXTURE0 + PONG_UNIT);
  gl.bindTexture(gl.TEXTURE_2D, pongTexture);

  let readUnit = 0;
  let writeFBO = pingFBO;

  for (let i = 0; i < passes; i++) {
    const horizontal = i % 2 === 0;

    gl.bindFramebuffer(gl.FRAMEBUFFER, writeFBO);
    gl.viewport(0, 0, canvasW, canvasH);
    gl.uniform2f(gl.getUniformLocation(blurProgram, 'u_direction'), horizontal ? 1.0 : 0.0, horizontal ? 0.0 : 1.0);
    gl.uniform1i(gl.getUniformLocation(blurProgram, 'u_image'), readUnit);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    if (writeFBO === pingFBO) {
      readUnit = PING_UNIT;
      writeFBO = pongFBO;
    } else {
      readUnit = PONG_UNIT;
      writeFBO = pingFBO;
    }
  }

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, canvasW, canvasH);
  gl.useProgram(compositeProgram);

  gl.uniform1i(gl.getUniformLocation(compositeProgram, 'u_sharp'), 0);
  gl.uniform1i(gl.getUniformLocation(compositeProgram, 'u_blurred'), readUnit);
  gl.uniform1i(gl.getUniformLocation(compositeProgram, 'u_mask'), 1);
  // Match the benchmark's blur path exactly — soft mask, no smoothstep.
  gl.uniform1f(gl.getUniformLocation(compositeProgram, 'u_sharpen'), 0.0);

  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.bindVertexArray(null);
}

// Pin the WASM CDN to the exact npm version we have installed. Using `@latest`
// can produce a JS/WASM ABI mismatch (the npm-installed JS bundle expects WASM
// exports from the same release), which surfaces as cryptic init crashes like
// `_emscripten_glClear: Cannot read properties of undefined (reading 'clear')`.
const TASKS_VISION_VERSION = '0.10.34';
const TASKS_VISION_WASM_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TASKS_VISION_VERSION}/wasm`;

let webGL2SegmenterPromise: Promise<ImageSegmenter> | null = null;

function getWebGL2Segmenter(): Promise<ImageSegmenter> {
  if (!webGL2SegmenterPromise) {
    const promise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(TASKS_VISION_WASM_BASE);
      return ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter_landscape/float16/latest/selfie_segmenter_landscape.tflite',
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        outputCategoryMask: false,
        outputConfidenceMasks: true,
      });
    })();
    // If the singleton init fails (e.g. transient GPU/WASM error, browser
    // context limit), drop the rejected promise so the next toggle can retry
    // fresh instead of cementing the failure for the whole page lifetime.
    promise.catch(() => {
      if (webGL2SegmenterPromise === promise) {
        webGL2SegmenterPromise = null;
      }
    });
    webGL2SegmenterPromise = promise;
  }
  return webGL2SegmenterPromise;
}

/* =========================================================================
 * Selfie Segmentation (Legacy WASM) engine — fallback path.
 * Mirrors SelfieSegPanel: CDN script, Emscripten-aware singleton.
 * ========================================================================= */

type GpuBuffer = HTMLCanvasElement | HTMLImageElement | ImageBitmap;

interface SelfieSegResults {
  image: GpuBuffer;
  segmentationMask: GpuBuffer;
}

interface SelfieSegInstance {
  close(): Promise<void>;
  onResults(listener: (results: SelfieSegResults) => void): void;
  initialize(): Promise<void>;
  reset(): void;
  send(inputs: { image: HTMLVideoElement }): Promise<void>;
  setOptions(options: { selfieMode?: boolean; modelSelection?: number }): void;
}

const SELFIE_CDN_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1.1675465747';
const SELFIE_SCRIPT_URL = `${SELFIE_CDN_BASE}/selfie_segmentation.js`;

const LEGACY_ABORT_PROPS = new Set([
  'arguments', 'thisProgram', 'quit', 'read', 'readAsync',
  'readBinary', 'setWindowTitle', 'wasmBinary', 'noExitRuntime', 'INITIAL_MEMORY',
  'wasmMemory', 'wasmModule', 'wasmTable', 'wasmExports',
  'HEAP8', 'HEAP16', 'HEAP32', 'HEAPU8', 'HEAPU16', 'HEAPU32', 'HEAPF32', 'HEAPF64',
]);

let selfieSegSuppressionInstalled = false;

function installSelfieSegSuppression(): void {
  if (selfieSegSuppressionInstalled) return;
  selfieSegSuppressionInstalled = true;
  const orig = Object.defineProperty;
  Object.defineProperty = function <T>(obj: T, prop: PropertyKey, desc: PropertyDescriptor & ThisType<T>): T {
    if (typeof prop === 'string' && LEGACY_ABORT_PROPS.has(prop) && desc && 'get' in desc && !('value' in desc) && desc.configurable === true) {
      return obj;
    }
    return orig.call(Object, obj, prop, desc) as T;
  } as typeof Object.defineProperty;
}

function loadSelfieSegScript(): Promise<void> {
  if ((window as any).SelfieSegmentation) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SELFIE_SCRIPT_URL}"]`);
    if (existing) {
      if ((window as any).SelfieSegmentation) { resolve(); return; }
      existing.addEventListener('load', () => resolve());
      return;
    }
    const script = document.createElement('script');
    script.src = SELFIE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load SelfieSegmentation script'));
    document.head.appendChild(script);
  });
}

let selfieSegPromise: Promise<SelfieSegInstance> | null = null;
let selfieSegResultsCallback: ((results: SelfieSegResults) => void) | null = null;

function getSelfieSegSegmenter(): Promise<SelfieSegInstance> {
  if (!selfieSegPromise) {
    installSelfieSegSuppression();
    selfieSegPromise = (async () => {
      await loadSelfieSegScript();
      const Ctor = (window as any).SelfieSegmentation;
      if (!Ctor) throw new Error('SelfieSegmentation not found after script load');
      const segmenter: SelfieSegInstance = new Ctor({
        locateFile: (file: string) => `${SELFIE_CDN_BASE}/${file}`,
      });
      segmenter.setOptions({ modelSelection: 1, selfieMode: false });
      segmenter.onResults((results: SelfieSegResults) => { selfieSegResultsCallback?.(results); });
      await segmenter.initialize();
      return segmenter;
    })();
  }
  return selfieSegPromise;
}

/* =========================================================================
 * Preload (VI-702, A2)
 * ========================================================================= */

/**
 * Prime the heavy ONE-TIME init — WASM fetch + segmentation-model download +
 * GPU-delegate creation — into the cached module-level singleton WITHOUT
 * starting any per-frame segmentation loop. Call this once the camera is on so
 * that toggling blur later (e.g. mid-countdown) skips the multi-second cold
 * start that otherwise leaves the blur button spinner stuck / the recording
 * pre-warm waiting on a first frame that never arrives in time.
 *
 * Fire-and-forget and idempotent: the singletons above are memoized (and
 * self-heal on rejection), so calling this when the engine is already warm is a
 * no-op, and a failed preload simply retries on the real toggle. There is NO
 * ongoing CPU/GPU cost — the render loop only runs while the hook is `enabled`;
 * this just gets the model/delegate resident and ready.
 */
export function preloadBlurEngine(engine: BlurEngine): void {
  if (engine === 'webgl2') {
    getWebGL2Segmenter().catch(() => {});
  } else if (engine === 'selfie-seg') {
    getSelfieSegSegmenter().catch(() => {});
  }
}

/* =========================================================================
 * Hook
 * ========================================================================= */

export function useBackgroundBlur({
  videoRef,
  canvasRef,
  engine,
  enabled,
  width: defaultWidth,
  height: defaultHeight,
}: UseBackgroundBlurArgs): UseBackgroundBlurResult {
  const [isReady, setIsReady] = useState(false);
  const [hasFrame, setHasFrame] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const glResRef = useRef<GLResources | null>(null);
  // Tracks which (canvas, w, h) combo glResRef was built for so we only
  // recreate WebGL2 resources when those actually change. Avoids needless
  // dispose+recreate churn on every enable toggle, which can race with
  // MediaPipe's GPU delegate runtime init in some browsers.
  const glConfigRef = useRef<{ canvas: HTMLCanvasElement; w: number; h: number } | null>(null);
  const webGL2SegmenterRef = useRef<ImageSegmenter | null>(null);

  const selfieSegRef = useRef<SelfieSegInstance | null>(null);
  const offscreenRef = useRef<HTMLCanvasElement | null>(null);
  const latestSelfieResultRef = useRef<SelfieSegResults | null>(null);
  const selfieResultVersionRef = useRef(0);

  const rafRef = useRef<number>(0);
  // Mirror of hasFrame for use inside RAF closures; avoids stale state reads.
  const hasFrameRef = useRef(false);

  // Probe the actual camera dims via loadedmetadata so the canvas pixel buffer
  // (and the engine FBOs / offscreen canvas) match the device's real output.
  // Mirrors the benchmark page's cameraDims pattern.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled) return;

    const update = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w > 0 && h > 0) {
        setDims((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
      }
    };

    update();
    video.addEventListener('loadedmetadata', update);
    video.addEventListener('resize', update);
    return () => {
      video.removeEventListener('loadedmetadata', update);
      video.removeEventListener('resize', update);
    };
  }, [videoRef, enabled]);

  useEffect(() => {
    if (!enabled || !engine || !canvasRef.current || !videoRef.current) {
      setIsReady(false);
      setHasFrame(false);
      hasFrameRef.current = false;
      return;
    }

    // Wait for the real camera dims before initializing anything. Falling back
    // to defaultWidth/Height here would size the canvas at e.g. 640x480 first,
    // then resize it to the actual 1280x720 a frame later when dims propagate.
    // canvas.captureStream() — which the recorder uses — would encode that as
    // a mid-stream resolution change in the saved S3 video (visible as the
    // picture appearing to grow/shrink mid-playback).
    if (dims.w <= 0 || dims.h <= 0) {
      setIsReady(false);
      setHasFrame(false);
      hasFrameRef.current = false;
      return;
    }
    const w = dims.w;
    const h = dims.h;

    // Only assign canvas dims when they actually differ. Reassigning width or
    // height (even to the same value) can reset the canvas's WebGL drawing
    // buffer in some browsers — avoid that when nothing has changed so we
    // don't disrupt MediaPipe's GPU delegate runtime.
    if (canvasRef.current.width !== w) canvasRef.current.width = w;
    if (canvasRef.current.height !== h) canvasRef.current.height = h;

    let cancelled = false;
    // Tracks the no-frame timeout for the WebGL2 path so the cleanup below
    // can clear it when the effect re-runs (engine swap, dims change, etc.).
    let webGL2FrameTimeoutId: number | null = null;
    setError(null);
    setIsReady(false);
    setHasFrame(false);
    hasFrameRef.current = false;

    if (engine === 'webgl2') {
      const initWebGL2 = async () => {
        try {
          // Reuse existing GL resources when the canvas + dims haven't changed.
          // Only dispose+recreate when the underlying config actually differs.
          const cfg = glConfigRef.current;
          const sameConfig =
            !!cfg && cfg.canvas === canvasRef.current && cfg.w === w && cfg.h === h;

          if (!sameConfig) {
            if (glResRef.current) {
              disposeGL(glResRef.current);
              glResRef.current = null;
            }
            if (canvasRef.current) {
              glResRef.current = initGL(canvasRef.current, w, h);
              glConfigRef.current = { canvas: canvasRef.current, w, h };
            }
          }
          if (!webGL2SegmenterRef.current) {
            webGL2SegmenterRef.current = await getWebGL2Segmenter();
          }
          if (cancelled) return;
          setIsReady(true);
          startWebGL2Loop();
        } catch (err: unknown) {
          if (!cancelled) {
            const msg = err instanceof Error ? err.message : String(err);
            setError(msg);
            console.error(
              '[Blur] WebGL2 init failed:',
              msg,
              getWebGL2DiagnosticInfo()
            );
          }
        }
      };

      const startWebGL2Loop = () => {
        const video = videoRef.current;
        const segmenter = webGL2SegmenterRef.current;
        const glRes = glResRef.current;
        if (!video || !segmenter || !glRes) return;

        let running = true;
        let lastTime = -1;

        // Hard deadline for first frame. On GPUs that lack the float-buffer
        // extensions MediaPipe needs (e.g. Mali-G52 / Galaxy A22) the WASM
        // postprocessor prints `RET_CHECK can_use_f32 || can_use_f16_backup`
        // to stderr on every segmentation call — but does NOT throw to JS, so
        // the per-frame catch below never fires. Result without this guard:
        // the spinner spins forever (VI-507). We cap on time and surface a
        // setError so the parent can disable blur + show a toast. The
        // upgraded hasWebGL2() capability check should already steer most
        // affected devices to Selfie Seg, but this is the defense in depth.
        const FRAME_TIMEOUT_MS = 10000;
        webGL2FrameTimeoutId = window.setTimeout(() => {
          if (cancelled || hasFrameRef.current) return;
          running = false;
          const msg = `WebGL2 blur did not produce a frame within ${FRAME_TIMEOUT_MS}ms`;
          console.error('[Blur]', msg, getWebGL2DiagnosticInfo());
          setError(msg);
        }, FRAME_TIMEOUT_MS);

        const processFrame = () => {
          if (!running || cancelled) return;

          // Wait for the <video> to be paint-ready. The benchmark page never
          // hits this case (its stream is immortal), but production restarts
          // the camera between questions; during the swap the element briefly
          // reports videoWidth/Height = 0 and readyState < HAVE_CURRENT_DATA,
          // which causes MediaPipe to crash with `texImage2D: no video` and
          // `ROI width and height must be > 0`. Skip cleanly and hide the
          // blur canvas so the live <video> below shows through instead of
          // a stale/black frame from the previous question.
          if (
            video.readyState < 2 ||
            video.videoWidth === 0 ||
            video.videoHeight === 0
          ) {
            if (hasFrameRef.current) {
              hasFrameRef.current = false;
              setHasFrame(false);
            }
            // Reset lastTime so the first frame of the new stream is processed
            // even if its currentTime happens to coincide with the old one.
            lastTime = -1;
            rafRef.current = requestAnimationFrame(processFrame);
            return;
          }

          const now = performance.now();
          if (video.currentTime === lastTime) {
            rafRef.current = requestAnimationFrame(processFrame);
            return;
          }
          lastTime = video.currentTime;

          try {
            const result = segmenter.segmentForVideo(video, now);
            if (result.confidenceMasks && result.confidenceMasks.length > 0) {
              const mpMask = result.confidenceMasks[0];
              const maskData = mpMask.getAsFloat32Array();
              const passes = Math.max(1, Math.round(getWebGL2BlurIntensity() / 2));
              renderBlurComposite(glRes, video, maskData, mpMask.width, mpMask.height, w, h, passes);
              if (!hasFrameRef.current) {
                hasFrameRef.current = true;
                setHasFrame(true);
                // Engine is alive — disarm the no-frame timeout. Subsequent
                // transient errors (camera restart, etc.) shouldn't trigger
                // the failure path; only an engine that NEVER produced a
                // frame should.
                if (webGL2FrameTimeoutId !== null) {
                  window.clearTimeout(webGL2FrameTimeoutId);
                  webGL2FrameTimeoutId = null;
                }
              }
            }
            result.close();
          } catch {
            // skip frame on transient errors
          }

          if (running) rafRef.current = requestAnimationFrame(processFrame);
        };

        rafRef.current = requestAnimationFrame(processFrame);
      };

      initWebGL2();

      return () => {
        cancelled = true;
        cancelAnimationFrame(rafRef.current);
        if (webGL2FrameTimeoutId !== null) {
          window.clearTimeout(webGL2FrameTimeoutId);
          webGL2FrameTimeoutId = null;
        }
      };
    }

    if (engine === 'selfie-seg') {
      if (!offscreenRef.current) {
        offscreenRef.current = document.createElement('canvas');
      }
      offscreenRef.current.width = w;
      offscreenRef.current.height = h;

      selfieSegResultsCallback = (r: SelfieSegResults) => {
        latestSelfieResultRef.current = r;
        selfieResultVersionRef.current += 1;
      };

      const initSelfieSeg = async () => {
        try {
          if (!selfieSegRef.current) {
            selfieSegRef.current = await getSelfieSegSegmenter();
          }
          if (cancelled) return;
          setIsReady(true);
          startSelfieSegLoop();
        } catch (err: unknown) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : String(err));
            console.error('[Blur] SelfieSeg init failed:', err);
          }
        }
      };

      const startSelfieSegLoop = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const offscreen = offscreenRef.current;
        const segmenter = selfieSegRef.current;
        if (!video || !canvas || !offscreen || !segmenter) return;

        const ctx = canvas.getContext('2d')!;
        const offCtx = offscreen.getContext('2d')!;
        let running = true;
        let lastSentTime = -1;
        let sending = false;
        let lastRenderedVersion = selfieResultVersionRef.current;

        const isVideoReady = () =>
          video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;

        const maybeSendFrame = () => {
          if (!running || cancelled || !video || !segmenter || sending) return;
          // Same readiness gate as the WebGL2 path — Selfie Seg's WASM also
          // crashes (or stalls) if we hand it a 0×0 frame during the camera
          // restart between questions.
          if (!isVideoReady()) return;
          if (video.currentTime === lastSentTime) return;
          lastSentTime = video.currentTime;
          sending = true;
          segmenter.send({ image: video }).catch(() => {}).finally(() => { sending = false; });
        };

        const renderLoop = () => {
          if (!running || cancelled) return;

          // Hide the blur canvas during the camera-restart gap so the live
          // <video> below shows through instead of the last frame from Q1.
          if (!isVideoReady()) {
            if (hasFrameRef.current) {
              hasFrameRef.current = false;
              setHasFrame(false);
            }
            lastSentTime = -1;
            rafRef.current = requestAnimationFrame(renderLoop);
            return;
          }

          maybeSendFrame();

          const result = latestSelfieResultRef.current;
          const currentVersion = selfieResultVersionRef.current;
          if (result && currentVersion !== lastRenderedVersion) {
            lastRenderedVersion = currentVersion;

            const intensity = getSelfieSegBlurIntensity();
            offCtx.save();
            offCtx.filter = `blur(${intensity}px)`;
            offCtx.drawImage(result.image, 0, 0, w, h);
            offCtx.restore();

            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(result.segmentationMask, 0, 0, w, h);
            ctx.globalCompositeOperation = 'source-in';
            ctx.drawImage(result.image, 0, 0, w, h);
            ctx.globalCompositeOperation = 'destination-over';
            ctx.drawImage(offscreen, 0, 0);
            ctx.globalCompositeOperation = 'source-over';

            if (!hasFrameRef.current) {
              hasFrameRef.current = true;
              setHasFrame(true);
            }
          }

          rafRef.current = requestAnimationFrame(renderLoop);
        };

        rafRef.current = requestAnimationFrame(renderLoop);
      };

      initSelfieSeg();

      return () => {
        cancelled = true;
        cancelAnimationFrame(rafRef.current);
        selfieSegResultsCallback = null;
      };
    }
  }, [enabled, engine, dims.w, dims.h, videoRef, canvasRef]);

  return {
    isReady,
    hasFrame,
    error,
    dims: { width: dims.w || defaultWidth, height: dims.h || defaultHeight },
  };
}
